import {
  Box,
  Menu,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Divider,
} from "@mui/material";
import { isNil } from "lodash";
import React, { useState, useMemo, useCallback, useEffect } from "react";

import FilterButton from "components/common/FilterButton";
import { useToast } from "contexts/ToastContext";
import { PatientsPaginationRequestDtoGenderEnum } from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface PatientFiltersProps {
  gender: PatientsPaginationRequestDtoGenderEnum[];
  ageFrom: number | null;
  ageTo: number | null;
  onGenderChange: (gender: PatientsPaginationRequestDtoGenderEnum[]) => void;
  onAgeRangeChange: (ageFrom: number | null, ageTo: number | null) => void;
}

const PatientFilters: React.FC<PatientFiltersProps> = ({
  gender,
  ageFrom,
  ageTo,
  onGenderChange,
  onAgeRangeChange,
}) => {
  const { colors } = useCelleraTheme();
  const { showError } = useToast();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [localAgeFrom, setLocalAgeFrom] = useState<string>(
    ageFrom?.toString() || "",
  );
  const [localAgeTo, setLocalAgeTo] = useState<string>(ageTo?.toString() || "");

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleGenderChange = useCallback(
    (genderValue: PatientsPaginationRequestDtoGenderEnum) => {
      const newGender = gender.includes(genderValue)
        ? gender.filter((g) => g !== genderValue)
        : [...gender, genderValue];
      onGenderChange(newGender);
    },
    [gender, onGenderChange],
  );

  const handleAgeFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalAgeFrom(value);
      const numValue = value ? parseInt(value, 10) : null;

      if (numValue !== null && (isNaN(numValue) || numValue < 0)) {
        return;
      }

      onAgeRangeChange(numValue, ageTo);
    },
    [ageTo, onAgeRangeChange],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (ageFrom !== null && ageTo !== null && ageFrom > ageTo) {
        showError(
          "Invalid Age Range",
          `Age "From" (${ageFrom}) cannot be greater than age "To" (${ageTo}).`,
        );
        return;
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [ageFrom, ageTo, showError]);

  const handleAgeToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalAgeTo(value);
      const numValue = value ? parseInt(value, 10) : null;

      if (numValue !== null && (isNaN(numValue) || numValue < 0)) {
        return;
      }

      onAgeRangeChange(ageFrom, numValue);
    },
    [ageFrom, onAgeRangeChange],
  );

  const handleClearAll = useCallback(() => {
    onGenderChange([]);
    onAgeRangeChange(null, null);
    setLocalAgeFrom("");
    setLocalAgeTo("");
  }, [onGenderChange, onAgeRangeChange]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (gender.length > 0) count++;
    if (ageFrom !== null || ageTo !== null) count++;
    return count;
  }, [gender.length, ageFrom, ageTo]);

  return (
    <Box className="flex items-center gap-2">
      <FilterButton
        activeFiltersCount={activeFiltersCount}
        onClick={handleClick}
      />

      <Menu
        anchorEl={anchorEl}
        open={!isNil(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              p: 2,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            {activeFiltersCount > 0 && (
              <Typography
                variant="body2"
                onClick={handleClearAll}
                sx={{
                  color: colors.primary.main,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Clear All
              </Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 1, color: colors.text.secondary }}
            >
              Gender
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gender.includes(
                      PatientsPaginationRequestDtoGenderEnum.Female,
                    )}
                    onChange={() =>
                      handleGenderChange(
                        PatientsPaginationRequestDtoGenderEnum.Female,
                      )
                    }
                  />
                }
                label="Female"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gender.includes(
                      PatientsPaginationRequestDtoGenderEnum.Male,
                    )}
                    onChange={() =>
                      handleGenderChange(
                        PatientsPaginationRequestDtoGenderEnum.Male,
                      )
                    }
                  />
                }
                label="Male"
              />
            </FormGroup>
          </Box>

          <Divider />

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Age From"
              type="number"
              size="small"
              value={localAgeFrom}
              onChange={handleAgeFromChange}
              fullWidth
            />
            <TextField
              label="Age To"
              type="number"
              size="small"
              value={localAgeTo}
              onChange={handleAgeToChange}
              fullWidth
            />
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default PatientFilters;
